<template>
  <div
    class="question-list-editor tree-question-editor"
    :class="{ 'job-editor-mode': displayMode === 'job-editor' }"
  >
    <!-- 问题列表（支持拖拽） -->
    <draggable
      v-model="rootQuestions"
      tag="div"
      class="question-list"
      handle=".item-btn-group .item-btn.drag-btn"
      animation="200"
      ghostClass="ghost"
      :disabled="disabled"
      @change="handleDraggableChange"
    >
      <template v-if="displayMode === 'job-editor'">
        <JobTreeQuestionNode
          v-for="(item, index) in rootQuestions"
          :key="item.id || item._vid || index"
          :question="item"
          :questions="questions"
          :disabled="disabled"
          :has-visibility-option="hasVisibilityOption"
          :visibility-option-list="visibilityOptionList"
          :child-question-parent-types="childQuestionParentTypes"
          :max-depth="MAX_TREE_DEPTH"
          :i18n-prefix="i18nPrefix"
          :root-only-question-actions="rootOnlyQuestionActions"
          :hide-existing-child-relation-actions="hideExistingChildRelationActions"
          :child-removal-mode="childRemovalMode"
          @edit="editChildQuestion"
          @delete="deleteQuestion"
          @open-child="openChildDialog"
          @remove-child="removeChild"
          @required-change="changeRequired"
          @visibility-change="handleVisibleChange"
        />
      </template>
      <template v-else>
      <div
        v-for="(item, index) in rootQuestions"
        class="list-item"
        :key="item.id || item._vid || index"
      >
        <div class="item-title">
          <div v-if="item.depth !== undefined && item.depth !== null && item.depth > 0" class="item-depth-tag">L{{ item.depth }}</div>
          <div class="item-name">{{ getQuestionTitle(item) }}</div>
          <div v-if="!disabled" class="item-btn-group">
            <el-dropdown v-if="hasVisibilityOption" class="item-btn visibility-btn" trigger="click" @command="handleVisibleChange(item, $event)">
              <div class="btn-inner-container">
                <span class="btn-label">{{ $t(`${i18nPrefix}.visibilityType.${getVisibilityTypeName(item.visibility)}`) }}</span>
                <i class="btn-icon el-icon-arrow-down el-icon--right"></i>
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="(option, vIndex) in visibilityOptionList" :key="vIndex" :command="option.value">{{ option.label }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <div class="item-btn drag-btn">
              <i class="iconfont iconMore"></i>
            </div>
            <button type="button" class="item-btn edit-btn" :aria-label="$t(`${i18nPrefix}.editor.question.title.edit`)" @click="editQuestion($event, item, index)">
              <i class="el-icon-edit-outline"></i>
            </button>
            <button type="button" class="item-btn delete-btn" :aria-label="$t(`${i18nPrefix}.message.deleteQuestionLabel`)" @click="deleteQuestion(item)">
              <i class="iconfont iconDelete"></i>
            </button>
          </div>
        </div>
        <!-- 选项列表（带子题配置） -->
        <div v-if="HAS_OPTIONS_QUESTION_TYPES.includes(item.type)" class="item-options-container">
          <ul class="option-list">
            <li
              v-for="(optionItem, optIndex) in item.options"
              class="option-item"
              :key="optIndex"
              :class="{ knockout: isKnockoutOption(optionItem) }"
            >
              <span>{{ getOptionDescription(optionItem) }}</span>
              <span v-if="getOptionChildLabel(item, optionItem)" class="next-question-preview">→ {{ getOptionChildLabel(item, optionItem) }}</span>
              <!-- 子题配置按钮 -->
              <el-button
                v-if="!disabled && !isKnockoutOption(optionItem) && canConfigureChildForParent(item)"
                type="text"
                class="option-action-btn"
                @click.stop="openChildDialog(item, optionItem, optIndex)"
              >
                {{ getOptionChildLabel(item, optionItem) ? $t(`${i18nPrefix}.btn.changeChild`) : $t(`${i18nPrefix}.btn.setChild`) }}
              </el-button>
              <el-button
                v-if="!disabled && getOptionChildLabel(item, optionItem)"
                type="text"
                class="option-action-btn remove-btn"
                @click.stop="removeChild(item, optionItem, optIndex)"
              >
                {{ $t(`${i18nPrefix}.btn.removeChild`) }}
              </el-button>
            </li>
          </ul>
          <!-- 嵌套子题展示 - 使用递归组件 -->
          <NestedChildQuestion
            :question="item"
            :depth="item.depth || 0"
            :depth-key="`root-${item.id || item._vid}`"
            :disabled="disabled"
            :max-depth="MAX_TREE_DEPTH"
            :has-options-question-types="HAS_OPTIONS_QUESTION_TYPES"
            :child-question-parent-types="childQuestionParentTypes"
            :i18n-prefix="i18nPrefix"
            :questions="questions"
            @open-dialog="openChildDialog"
            @remove-child="removeChild"
            @edit-question="editChildQuestion"
            @delete-question="deleteQuestion"
          />
        </div>
      </div>
      </template>
    </draggable>

    <!-- 添加按钮组 -->
    <div class="add-btn-group">
      <el-button
        v-for="(btnItem, btnIndex) in addBtnList"
        v-show="btnItem.show"
        class="add-btn"
        type="text"
        :disabled="disabled"
        :key="btnIndex"
        :class="btnItem.class"
        @click="handleAddBtnClick($event, btnItem)"
      >
        {{ btnItem.label }}
      </el-button>
    </div>

    <!-- 添加/编辑问题对话框 -->
    <DialogWrapper
      close-on-cancel
      append-to-body
      class="editor-dialog question-editor"
      :title="$t(`${i18nPrefix}.editor.question.title.${questionEditor.isEdit ? 'edit' : 'add'}`)"
      :show.sync="questionEditor.show"
      :close-on-click-modal="false"
      :loading="saving"
      @save="handleSaveQuestion"
    >
      <QuestionEditor
        ref="questionEditor"
        :template-name="templateName"
        :has-visibility-option="hasVisibilityOption"
        :default-visibility="defaultVisibility"
        :disabled-question-type="disabledQuestionType"
        :disable-question-type="editingQuestionHasChildren"
        :is-edit="questionEditor.isEdit"
        :question="questionEditor.question"
        :question-list="questions"
        :current-question-index="questionEditor.index"
        :show-required-asterisk="showRequiredAsterisk"
      />
    </DialogWrapper>

    <ConfirmDialog
      append-to-body
      :show.sync="treeConfirmation.show"
      :loading="treeConfirmation.loading"
      :title="$t('common.message.warning')"
      :content="$t(`${i18nPrefix}.message.${treeConfirmation.messageKey}`)"
      :save-btn-label-i18n-key="treeConfirmation.type === 'delete' ? 'common.btn.delete' : 'common.btn.confirm'"
      @confirm="confirmTreeAction"
      @cancel="cancelTreeAction"
    />

    <!-- 从模板选择对话框 -->
    <DialogWrapper
      close-on-cancel
      append-to-body
      class="selector-dialog group-selector"
      destroy-on-close
      :show.sync="groupSelector.show"
      :close-on-click-modal="false"
      :title="$t(`${i18nPrefix}.editor.question.title.add`)"
      @save="handleSelectedQuestion"
    >
      <GroupQuestionSelector
        ref="groupQuestionSelector"
        v-model="groupSelector.selected"
        tree-mode
        :hide-unavailable-trees="hideUnavailableTrees"
        :disabled-question-type="disabledQuestionType"
        :data="groupList"
      />
    </DialogWrapper>

    <!-- 子题配置对话框 -->
    <DialogWrapper
      append-to-body
      :title="$t(`${i18nPrefix}.editor.subQuestion.title.${isEditingChild ? 'edit' : 'add'}`)"
      :show.sync="childDialogVisible"
      :loading="savingChild"
      @save="saveChildQuestion"
    >
      <QuestionEditor
        ref="childQuestionEditor"
        :is-edit="isEditingChild"
        :question-type-label="$t(`${i18nPrefix}.label.subQuestionType`)"
        :has-visibility-option="hasVisibilityOption"
        :default-visibility="defaultVisibility"
        :disabled-question-type="disabledQuestionType"
        :disable-question-type="editingChildHasChildren"
        :question="newChildQuestion"
        :relation-fields="childRelationFields"
        :show-required-asterisk="showRequiredAsterisk"
      />
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import { HAS_OPTIONS_QUESTION_TYPES, TREE_SUPPORTED_QUESTION_TYPES, VISIBILITY_TYPE } from '@/constant/openQuestion'
import QuestionEditor, { visibilityOptionList } from './questionEditor.vue'
import GroupQuestionSelector from './groupQuestionSelector.vue'
import NestedChildQuestion from './nestedChildQuestion.vue'
import JobTreeQuestionNode from './jobTreeQuestionNode.vue'
import { i18nPrefix } from './common'
import { mapActions } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import {
  MAX_TREE_DEPTH,
  canAttachQuestionAsChild,
  cloneSelectedQuestionSubtrees,
  getComparableOptionKey,
  getComparableQuestionKey,
  getOptionChildKey,
  getQuestionKey,
  getRootQuestions,
  normalizeEditableQuestionTree,
  removeQuestionSubtree,
  reorderRootSubtrees,
  setQuestionSubtreeVisibility
} from '@/utils/openQuestionTree'

export default {
  name: 'TreeQuestionEditor',

  model: {
    prop: 'value',
    event: 'change'
  },

  components: {
    draggable,
    QuestionEditor,
    GroupQuestionSelector,
    NestedChildQuestion,
    JobTreeQuestionNode,
    ConfirmDialog,
    DialogWrapper
  },

  props: {
    value: {
      type: Array,
      required: true
    },
    groupList: {
      type: Array,
      default: () => []
    },
    templateName: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    },
    hasVisibilityOption: {
      type: Boolean,
      default: false
    },
    defaultVisibility: {
      type: String,
      default: VISIBILITY_TYPE.IE
    },
    surveyType: {
      type: String,
      default: 'standard'
    },
    jobId: {
      type: Number,
      default: null
    },
    hiddenQuestionTemplate: {
      type: Boolean,
      default: false
    },
    disableQuestionGroup: {
      type: Boolean,
      default: false
    },
    hideUnavailableTrees: {
      type: Boolean,
      default: false
    },
    displayMode: {
      type: String,
      default: ''
    },
    childQuestionParentTypes: {
      type: Array,
      default: () => TREE_SUPPORTED_QUESTION_TYPES
    },
    showRequiredAsterisk: {
      type: Boolean,
      default: false
    },
    rootOnlyQuestionActions: {
      type: Boolean,
      default: false
    },
    hideExistingChildRelationActions: {
      type: Boolean,
      default: false
    },
    childRemovalMode: {
      type: String,
      default: 'detach-immediately',
      validator: value => ['detach-immediately', 'delete-subtree-on-save'].includes(value)
    }
  },

  data () {
    return {
      i18nPrefix,
      treeConfirmation: { show: false, loading: false, type: null, questionKey: null, optionKey: null, childKey: null, messageKey: 'confirmRemoveChild' },
      HAS_OPTIONS_QUESTION_TYPES,
      MAX_TREE_DEPTH,
      visibilityOptionList,
      questions: [],
      saving: false,
      savingChild: false,
      questionEditor: {
        show: false,
        isEdit: false,
        question: null,
        index: -1
      },
      groupSelector: {
        show: false,
        selected: []
      },
      childDialogVisible: false,
      currentParentQuestion: null,
      currentParentQuestionKey: null,
      currentOptionKey: null,
      currentChildQuestionKey: null,
      childDialogSessionId: 0,
      editingChildQuestion: null,
      newChildQuestion: {
        title: '',
        type: null,
        options: [],
        depth: 1
      }
    }
  },

  computed: {
    addBtnList () {
      return [
        {
          show: true,
          addFromTemplate: false,
          label: this.$t(`${this.i18nPrefix}.btn.addQuestion`)
        },
        {
          show: !this.hiddenQuestionTemplate,
          addFromTemplate: true,
          label: this.$t(`${this.i18nPrefix}.btn.addQuestionFromTemplate`)
        }
      ]
    },
    // 是否正在编辑已有子题
    isEditingChild () {
      return !!this.editingChildQuestion
    },
    editingQuestionHasChildren () {
      if (!this.questionEditor.isEdit || this.questionEditor.index < 0) return false
      return this.hasQuestionChildren(this.questions[this.questionEditor.index])
    },
    editingChildHasChildren () {
      if (!this.editingChildQuestion) return false
      const childKey = this.getComparableQuestionKey(this.editingChildQuestion)
      const question = _.find(this.questions, item => this.getComparableQuestionKey(item) === childKey)
      return this.hasQuestionChildren(question)
    },
    currentTriggerOptionDescription () {
      const target = this.findChildDialogTarget()
      return this.getOptionDescription(target && target.option)
    },
    childRelationFields () {
      return {
        parentQuestion: {
          value: this.getQuestionTitle(this.currentParentQuestion),
          disabled: true
        },
        triggerOption: {
          value: this.currentTriggerOptionDescription,
          control: 'input',
          disabled: true
        }
      }
    },
    // 只显示根问题（过滤掉子问题）
    rootQuestions: {
      get () {
        return getRootQuestions(this.questions)
      },
      set (rootQuestions) {
        this.questions = reorderRootSubtrees(this.questions, rootQuestions, this.generateTempVid)
      }
    }
  },

  watch: {
    value: {
      immediate: true,
      handler () {
        if (this.value && this.value.length > 0) {
          this.questions = this.normalizeQuestionTree(this.value)
        } else {
          this.questions = []
        }
      }
    }
  },

  methods: {
    ...mapActions('openQuestion', [
      'removeOptionNextQuestion'
    ]),

    hasQuestionChildren (question) {
      return Boolean(question && _.some(question.options || [], option => getOptionChildKey(option)))
    },

    // 生成临时 ID
    generateTempVid () {
      return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    },

    getQuestionKey (question) {
      return getQuestionKey(question)
    },

    getComparableQuestionKey (questionOrKey) {
      return getComparableQuestionKey(questionOrKey)
    },

    getOptionChildKey (option) {
      return getOptionChildKey(option)
    },
    getQuestionTitle (question) {
      return question && question.title
    },
    getOptionDescription (option) {
      return option && option.description
    },
    changeRequired (question) {
      if (this.disabled) return
      const key = this.getComparableQuestionKey(question)
      const matches = _.filter(this.questions, item => this.getComparableQuestionKey(item) === key)
      if (!key || matches.length !== 1) return
      const target = matches[0]
      this.$set(target, 'required', !target.required)
      this.emitChange()
    },

    isKnockoutOption (option) {
      return option && (option.is_knockout === true || option.is_knockout === 1 || option.is_knockout === '1')
    },

    canConfigureChildForParent (question) {
      return Boolean(question) && this.childQuestionParentTypes.includes(question.type)
    },

    normalizeQuestionTree (questionList) {
      return normalizeEditableQuestionTree(questionList, this.generateTempVid)
    },

    getNormalizedQuestions () {
      return _.cloneDeep(this.questions)
    },

    // 获取类型标签
    getTypeLabel (type) {
      return this.$t(`${this.i18nPrefix}.questionTypeShort.${type}`)
    },

    // 获取可见性类型名称
    getVisibilityTypeName (visibility) {
      return _.findKey(VISIBILITY_TYPE, (value) => value === visibility)
    },

    // 获取选项子题标签
    getOptionChildLabel (parentQuestion, optionItem) {
      if (optionItem.child_question) {
        return this.getQuestionTitle(optionItem.child_question)
      }
      if (optionItem.child_question_id) {
        const targetQ = _.find(this.questions, q => q.id === optionItem.child_question_id)
        return this.getQuestionTitle(targetQ)
      }
      if (optionItem.child_question_vid) {
        const targetQ = _.find(this.questions, q => q._vid === optionItem.child_question_vid)
        return this.getQuestionTitle(targetQ)
      }
      if (optionItem.next_question_id) {
        const targetQ = _.find(this.questions, q => q.id === optionItem.next_question_id)
        return this.getQuestionTitle(targetQ)
      }
      return ''
    },

    // 拖拽变更
    handleDraggableChange () {
      this.emitChange()
    },

    // 可见性变更
    handleVisibleChange (questionItem, visibility) {
      if (this.disabled) return
      setQuestionSubtreeVisibility(this.questions, questionItem, visibility)
      this.emitChange()
    },

    // 编辑问题
    editQuestion ($event, questionItem) {
      const actualIndex = _.findIndex(this.questions, q => this.getQuestionKey(q) === this.getQuestionKey(questionItem))
      if (actualIndex < 0) {
        return
      }
      this.questionEditor.isEdit = true
      this.questionEditor.question = _.cloneDeep(questionItem)
      this.questionEditor.index = actualIndex
      this.questionEditor.show = true
    },

    // 编辑子问题
    editChildQuestion (childQuestion) {
      // 在 questions 数组中找到该子问题的索引
      const index = _.findIndex(this.questions, q => (q.id || q._vid) === (childQuestion.id || childQuestion._vid))
      if (index >= 0) {
        this.questionEditor.isEdit = true
        this.questionEditor.question = _.cloneDeep(this.questions[index])
        this.questionEditor.index = index
        this.questionEditor.show = true
      }
    },

    // 删除当前问题及其完整子树，同时解除树外父选项对该子树的引用
    async deleteQuestion (questionItem) {
      if (this.treeConfirmation.loading) return
      const result = removeQuestionSubtree(this.questions, questionItem)
      if (!result.removedKeys.length) return
      this.treeConfirmation = {
        show: true,
        loading: false,
        type: 'delete',
        questionKey: this.getQuestionKey(questionItem),
        removedKeys: result.removedKeys,
        messageKey: result.removedKeys.length > 1 ? 'confirmDeleteQuestionTree' : 'confirmDeleteQuestion'
      }
    },

    // 添加按钮点击
    handleAddBtnClick ($event, btnItem) {
      if (!btnItem.show) return

      if (btnItem.addFromTemplate) {
        this.groupSelector.selected = []
        this.groupSelector.show = true
        if (this.$refs.groupQuestionSelector) {
          this.$refs.groupQuestionSelector.refreshGroupList()
        }
      } else {
        this.questionEditor.isEdit = false
        this.questionEditor.question = null
        this.questionEditor.index = -1
        this.questionEditor.show = true
        // 新增: 在弹窗打开后显式清空表单，防止回显上次问题信息
        this.$nextTick(() => {
          if (this.$refs.questionEditor && this.$refs.questionEditor.clearData) {
            this.$refs.questionEditor.clearData()
          }
        })
      }
    },

    // 保存问题（添加/编辑）
    async handleSaveQuestion () {
      this.saving = true
      try {
        const editorRef = this.$refs.questionEditor
        const questionData = await editorRef.submit()

        if (!questionData) {
          return
        }

        const editingQuestion = this.questionEditor.isEdit ? this.questions[this.questionEditor.index] : null
        const candidate = { ...editingQuestion, ...questionData }
        if (this.checkQuestionDuplicate(this.questions, candidate)) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.duplicateTitle`))
          return
        }

        if (!questionData.id && !questionData._vid) {
          questionData._vid = this.generateTempVid()
        }

        if (questionData.options) {
          _.forEach(questionData.options, (opt, idx) => {
            if (!opt.id && !opt._vid) {
              opt._vid = `${questionData._vid}_opt_${idx}`
            }
          })
        }

        if (this.questionEditor.isEdit) {
          const index = this.questionEditor.index
          this.$delete(this.questions[index], 'title')
          _.assign(this.questions[index], questionData)
        } else {
          // A newly created root follows the management page's active default.
          // Existing/template questions retain their persisted status.
          questionData.status = 1
          questionData.depth = 0
          questionData.order = this.questions.length + 1
          this.questions.push(questionData)
        }

        this.questions = this.normalizeQuestionTree(this.questions)
        this.emitChange()
        this.questionEditor.show = false
        this.$message.success(this.$t('common.message.success'))
      } finally {
        this.saving = false
      }
    },

    // 从模板选择问题
    handleSelectedQuestion () {
      const selected = this.groupSelector.selected
      const newQuestionList = []
      let hasDuplicate = false
      let hasInvalidTree = false

      _.forEach(selected, selectedRoot => {
        const sourceQuestions = selectedRoot._parent
          ? selectedRoot._parent.question_templates
          : [selectedRoot]
        const cloneResult = cloneSelectedQuestionSubtrees({
          selectedRoots: [selectedRoot],
          sourceQuestions,
          generateTempVid: this.generateTempVid,
          startOrder: this.questions.length + newQuestionList.length + 1,
          hasVisibilityOption: this.hasVisibilityOption,
          defaultVisibility: this.defaultVisibility
        })

        if (cloneResult.errors) {
          hasInvalidTree = true
          return
        }
        const duplicateQuestion = _.find(cloneResult.questions.filter(question => !question.depth), question => {
          return this.checkQuestionDuplicate([...this.questions, ...newQuestionList], question)
        })
        if (duplicateQuestion) {
          hasDuplicate = true
          return
        }
        newQuestionList.push(...cloneResult.questions)
      })

      if (hasDuplicate) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.duplicateTitle`))
      }
      if (hasInvalidTree) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.invalidTemplateTree`))
      }

      this.questions = this.normalizeQuestionTree([...this.questions, ...newQuestionList])
      this.groupSelector.show = false
      this.emitChange()
    },

    checkQuestionDuplicate (questionList, questionData) {
      const childKeys = new Set(_.flatMap(questionList, question =>
        (question.options || []).map(option => this.getOptionChildKey(option)).filter(Boolean).map(String)))
      if (questionData.depth > 0 || childKeys.has(String(this.getQuestionKey(questionData)))) return false
      return _.find(questionList, (item) => {
        return !childKeys.has(String(this.getQuestionKey(item))) && !item.depth &&
          this.getQuestionKey(item) !== this.getQuestionKey(questionData) &&
          item.title === questionData.title
      })
    },

    // 打开子题配置对话框
    openChildDialog (parentQuestion, option) {
      const parentKey = this.getComparableQuestionKey(parentQuestion)
      const optionKey = getComparableOptionKey(option)
      const target = this.findChildDialogTarget(parentKey, optionKey)
      if (this.disabled || !target || this.isKnockoutOption(target.option) ||
        !this.canConfigureChildForParent(target.question)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionNotFound`))
        return
      }
      this.currentParentQuestion = target.question
      this.currentParentQuestionKey = parentKey
      this.currentOptionKey = optionKey
      this.currentChildQuestionKey = this.getComparableQuestionKey(this.getOptionChildKey(target.option))
      this.childDialogSessionId += 1

      const childQuestion = target.option.child_question

      // 如果已有子题（无论是新建的还是已保存的），直接进入编辑模式
      if (childQuestion) {
        // 编辑模式：回显子题内容
        this.newChildQuestion = _.cloneDeep(childQuestion)
        // 标记为编辑模式
        this.editingChildQuestion = childQuestion
      } else {
        // 没有子题：打开创建tab（新建模式）
        this.newChildQuestion = {
          title: '',
          type: null,
          options: [],
          depth: (target.question.depth || 0) + 1
        }
        this.editingChildQuestion = null
      }

      this.childDialogVisible = true
    },
    openFirstAvailableChildDialog (questionKey) {
      const parent = _.find(this.questions, question => this.getQuestionKey(question) === questionKey)
      const option = parent && _.find(parent.options || [], item => !this.isKnockoutOption(item) && !getOptionChildKey(item))
      if (!parent || !option) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.noAvailableChildOption`))
        return
      }
      this.openChildDialog(parent, option)
    },

    getChildDialogSaveContext () {
      return {
        sessionId: this.childDialogSessionId,
        parentKey: this.currentParentQuestionKey,
        optionKey: this.currentOptionKey,
        originalChildKey: this.currentChildQuestionKey,
        isEditingChild: Boolean(this.editingChildQuestion)
      }
    },

    isCurrentChildDialogSave (context) {
      return this.childDialogSessionId === context.sessionId &&
        this.currentParentQuestionKey === context.parentKey &&
        this.currentOptionKey === context.optionKey &&
        this.currentChildQuestionKey === context.originalChildKey &&
        Boolean(this.editingChildQuestion) === context.isEditingChild
    },

    findChildDialogTarget (parentKey = this.currentParentQuestionKey, optionKey = this.currentOptionKey) {
      if (!parentKey || !optionKey) return null
      const matchingQuestions = _.filter(this.questions, item => this.getComparableQuestionKey(item) === parentKey)
      if (matchingQuestions.length !== 1) return null
      const question = matchingQuestions[0]
      const matchingIndexes = _.reduce(question.options || [], (indexes, option, index) => {
        if (getComparableOptionKey(option) === optionKey) indexes.push(index)
        return indexes
      }, [])
      if (matchingIndexes.length !== 1) return null
      const index = matchingIndexes[0]
      return { question, option: question.options[index], index }
    },

    isOriginalChildDialogTarget (target, originalChildKey = this.currentChildQuestionKey) {
      return Boolean(target) &&
        this.getComparableQuestionKey(this.getOptionChildKey(target.option)) === originalChildKey
    },

    canSaveChildQuestion (target, questionData, childKey, isNew) {
      if (!target || !this.canConfigureChildForParent(target.question)) return false
      const prospectiveQuestions = _.cloneDeep(this.questions)
      const parentKey = this.getComparableQuestionKey(target.question)
      const parent = _.find(prospectiveQuestions, question => this.getComparableQuestionKey(question) === parentKey)
      const option = parent && _.find(parent.options || [], item => getComparableOptionKey(item) === getComparableOptionKey(target.option))
      if (!parent || !option) return false

      let childQuestion
      if (isNew) {
        childQuestion = _.cloneDeep(questionData)
        prospectiveQuestions.push(childQuestion)
      } else {
        childQuestion = _.find(prospectiveQuestions, question => this.getComparableQuestionKey(question) === childKey)
        if (!childQuestion) return false
        _.assign(childQuestion, _.cloneDeep(questionData))
      }
      option.child_question = childQuestion
      option.child_question_id = childQuestion.id || null
      option.child_question_vid = childQuestion._vid || null
      option.next_question_id = childQuestion.id || null
      return canAttachQuestionAsChild(prospectiveQuestions, parentKey, childKey)
    },

    // 保存子题配置
    async saveChildQuestion () {
      if (this.savingChild) return
      if (this.disabled) {
        this.childDialogVisible = false
        return
      }
      const context = this.getChildDialogSaveContext()
      this.savingChild = true

      try {
        let target = this.findChildDialogTarget(context.parentKey, context.optionKey)
        if (!this.isCurrentChildDialogSave(context) ||
          !this.isOriginalChildDialogTarget(target, context.originalChildKey) || this.isKnockoutOption(target.option) ||
          !this.canConfigureChildForParent(target.question)) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionNotFound`))
          return
        }

        const editorRef = this.$refs.childQuestionEditor
        const newQuestionData = await editorRef.submit()

        if (!newQuestionData) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.fillQuestionRequired`))
          return
        }

        target = this.findChildDialogTarget(context.parentKey, context.optionKey)
        if (!this.isCurrentChildDialogSave(context) ||
          !this.isOriginalChildDialogTarget(target, context.originalChildKey) || this.isKnockoutOption(target.option) ||
          !this.canConfigureChildForParent(target.question)) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionNotFound`))
          return
        }

        // 如果是编辑已有的新建子题
        if (context.isEditingChild) {
          const existingIndex = _.findIndex(this.questions, q =>
            this.getComparableQuestionKey(q) === context.originalChildKey
          )

          if (existingIndex >= 0) {
            const existingQuestion = this.questions[existingIndex]
            // 更新已有问题的数据
            newQuestionData._vid = existingQuestion._vid
            newQuestionData.id = existingQuestion.id
            newQuestionData.depth = existingQuestion.depth
            newQuestionData.is_terminal = !_.includes(TREE_SUPPORTED_QUESTION_TYPES, newQuestionData.type)
            newQuestionData.order = existingQuestion.order

            if (newQuestionData.options) {
              _.forEach(newQuestionData.options, (opt, idx) => {
                if (!opt.id && !opt._vid) {
                  opt._vid = `${newQuestionData._vid}_opt_${idx}`
                }
              })
            }

            if (!this.canSaveChildQuestion(target, newQuestionData, context.originalChildKey, false)) {
              this.$message.warning(this.$t(`${this.i18nPrefix}.message.cycleDetected`))
              return
            }

            // 更新 questions 数组中的数据
            this.$delete(this.questions[existingIndex], 'title')
            _.assign(this.questions[existingIndex], newQuestionData)

            // 更新父选项中的 child_question 引用
            this.$set(target.option, 'child_question', this.questions[existingIndex])

            this.emitChange()
          } else {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionNotFound`))
            return
          }
        } else {
          // 创建新的子题
          newQuestionData._vid = this.generateTempVid()
          newQuestionData.depth = (target.question.depth || 0) + 1
          newQuestionData.is_terminal = !_.includes(TREE_SUPPORTED_QUESTION_TYPES, newQuestionData.type)
          newQuestionData.order = this.questions.length + 1

          if (newQuestionData.options) {
            _.forEach(newQuestionData.options, (opt, idx) => {
              opt._vid = `${newQuestionData._vid}_opt_${idx}`
            })
          }

          if (!this.canSaveChildQuestion(target, newQuestionData, this.getComparableQuestionKey(newQuestionData), true)) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.cycleDetected`))
            return
          }

          this.questions.push(newQuestionData)

          this.$set(target.option, 'child_question', newQuestionData)
          this.$set(target.option, 'child_question_vid', newQuestionData._vid)
          this.$set(target.option, 'next_question_id', null)

          this.emitChange()
        }
        this.childDialogVisible = false
        this.$message.success(this.$t('common.message.success'))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save child question:', error)
        if (!error.code) {
          this.$message.error(this.$t('common.message.error') + ': ' + (error.message || 'Unknown error'))
        }
      } finally {
        this.savingChild = false
      }
    },

    // 移除子题
    async removeChild (parentQuestion, option, optIndex) {
      if (this.disabled) return
      if (this.treeConfirmation && this.treeConfirmation.loading) return
      if (this.childRemovalMode === 'delete-subtree-on-save') {
        const childKey = this.getOptionChildKey(option)
        const childQuestion = this.questions.find(question => String(this.getQuestionKey(question)) === String(childKey))
        if (childQuestion) {
          this.deleteQuestion(childQuestion)
        }
        return
      }
      if (!this.canDetachChild(parentQuestion, option, optIndex)) return
      this.treeConfirmation = {
        show: true,
        loading: false,
        type: 'remove',
        questionKey: this.getQuestionKey(parentQuestion),
        optionKey: option.id || option._vid,
        childKey: this.getOptionChildKey(option),
        messageKey: 'confirmRemoveChild'
      }
    },

    canDetachChild (parentQuestion, option, optIndex) {
      const childKey = this.getOptionChildKey(option)
      const detachedQuestions = _.cloneDeep(this.questions)
      const detachedParent = detachedQuestions.find(question => String(this.getQuestionKey(question)) === String(this.getQuestionKey(parentQuestion)))
      if (!detachedParent || !detachedParent.options[optIndex] || !childKey) return false
      Object.assign(detachedParent.options[optIndex], {
        child_question: null, child_question_id: null, child_question_vid: null, next_question_id: null
      })
      const normalized = this.normalizeQuestionTree(detachedQuestions)
      const detachedChild = normalized.find(question => String(this.getQuestionKey(question)) === String(childKey))
      if (detachedChild && this.checkQuestionDuplicate(normalized, detachedChild)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.duplicateTitle`))
        return false
      }
      return true
    },

    cancelTreeAction () {
      if (!this.treeConfirmation.loading) this.treeConfirmation.show = false
    },

    findConfirmationTarget (pending) {
      const question = this.questions.find(item => String(this.getQuestionKey(item)) === String(pending.questionKey))
      if (!question) return null
      if (pending.type === 'delete') return { question }
      if (!pending.optionKey) return null
      const index = (question.options || []).findIndex(option => String(option.id || option._vid) === String(pending.optionKey))
      const option = question.options[index]
      if (!option || String(this.getOptionChildKey(option)) !== String(pending.childKey)) return null
      return { question, option, index }
    },

    async confirmTreeAction () {
      const pending = this.treeConfirmation
      if (!pending.show || pending.loading) return
      let target = this.findConfirmationTarget(pending)
      if (!target) {
        pending.show = false
        return
      }
      if (pending.type === 'delete') {
        const result = removeQuestionSubtree(this.questions, target.question)
        // 编辑态发生变化时，让用户重新确认当前删除范围。
        if (!_.isEqual(result.removedKeys, pending.removedKeys)) {
          pending.removedKeys = result.removedKeys
          pending.messageKey = result.removedKeys.length > 1 ? 'confirmDeleteQuestionTree' : 'confirmDeleteQuestion'
          return
        }
        pending.loading = true
        try {
          this.questions = this.normalizeQuestionTree(result.questions)
          _.forEach(this.questions, (question, index) => { question.order = index + 1 })
          this.emitChange()
          pending.show = false
        } finally {
          pending.loading = false
        }
        return
      }
      if (!this.canDetachChild(target.question, target.option, target.index)) return
      pending.loading = true
      try {
        const childQuestionId = target.option.child_question_id || (target.option.child_question && target.option.child_question.id)
        const hasRealChildId = childQuestionId && !childQuestionId.toString().startsWith('temp_')
        if (target.question.id && target.option.id && this.jobId && hasRealChildId) {
          await this.removeOptionNextQuestion({ jobId: this.jobId, questionId: target.question.id, optionId: target.option.id })
        }
        target = this.findConfirmationTarget(pending)
        if (target) {
          Object.assign(target.option, { child_question: null, child_question_id: null, child_question_vid: null, next_question_id: null })
          this.questions = this.normalizeQuestionTree(this.questions)
          this.emitChange()
          this.$message.success(this.$t('common.message.success'))
        }
        pending.show = false
      } catch (error) {
        this.$message.error(this.$t('common.message.error'))
      } finally {
        pending.loading = false
      }
    },

    emitChange () {
      this.$emit('input', this.questions)
      this.$emit('change', this.questions)
    }
  }
}
</script>

<style lang="scss" scoped>
.question-list-editor.tree-question-editor {
  position: relative;

  .next-question-preview {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .question-list {
    margin-top: 20px;
    margin-bottom: 40px;

    .list-item {
      padding: 5px 10px;
      border: 1px solid #e7e7e7;
      background: #f1f1f1;

      &:not(:first-child) {
        margin-top: 10px;
      }

      &.ghost {
        opacity: 0.5;
        background-color: #cecece;
      }

      .item-title {
        display: flex;
        line-height: 16px;
        align-items: center;

        .item-name {
          flex: 1;
          min-width: 0;
          overflow-wrap: anywhere;
          word-break: break-word;
          margin-left: 10px;
          color: #3b3b3b;
        }

        .item-depth-tag {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 34px;
          height: 22px;
          border-radius: 999px;
          background: #eef5ff;
          color: #0f6fc6;
          font-size: 12px;
          font-weight: 600;
        }

        .item-btn-group {
          flex-shrink: 0;
          align-self: flex-start;
          display: flex;
          color: #0096cc;
          align-items: center;

          .item-btn {
            cursor: pointer;
            width: 24px;
            height: 24px;
            padding: 0;
            border: 0;
            background: transparent;
            color: inherit;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            i {
              font-size: 18px;
            }

            &:not(:first-child) {
              margin-left: 10px;
            }
          }

          .drag-btn {
            cursor: grab;
          }

          .visibility-btn {
            width: auto;
            color: inherit;
            font-size: 12px;

            .btn-inner-container {
              .btn-label {
                vertical-align: middle;
              }

              .btn-icon {
                font-size: 14px;
                vertical-align: middle;
              }
            }
          }
        }
      }

      .item-options-container {
        color: #676767;
        margin-top: 5px;

        .option-list {
          padding-left: 15px;

          .option-item {
            padding: 3px 0;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;

            &::before {
              content: '-';
              margin-right: 10px;
            }

            &.knockout {
              color: #F56C6C;
            }

            .next-question-preview {
              color: #0096cc;
            }

            .option-action-btn {
              padding: 0;
              font-size: 12px;

              &.remove-btn {
                color: #f56c6c;
              }
            }
          }
        }

        // 子问题预览样式 - 紧密连接父问题
        .child-question-preview {
          margin-left: 15px;
          margin-top: 0;
          padding: 4px 8px;
          background: #fafafa;
          border-left: 2px solid #0096cc;
          display: flex;
          flex-direction: column;
          gap: 4px;

          .child-header {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;

            .child-trigger {
              color: #909399;
              font-size: 12px;
            }

            .child-depth {
              background: #0096cc;
              color: #fff;
              padding: 1px 4px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: bold;
            }

            .child-title {
              color: #606266;
              font-size: 13px;
            }

            .child-type {
              color: #909399;
              font-size: 11px;
            }

            .edit-child-btn {
              padding: 0;
              font-size: 14px;
              color: #0096cc;
              margin-left: 4px;

              i {
                font-size: 14px;
              }
            }
          }

          // 子问题的选项列表容器
          .child-options-container {
            margin-top: 4px;
            padding-left: 10px;

            .child-option-list {
              padding-left: 15px;
              margin: 0;
              list-style: none;

              li {
                padding: 2px 0;
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 6px;

                &::before {
                  content: '-';
                  margin-right: 8px;
                }

                .next-question-preview {
                  color: #0096cc;
                  font-size: 12px;
                }

                .option-action-btn {
                  padding: 0;
                  font-size: 11px;

                  &.remove-btn {
                    color: #f56c6c;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .add-btn-group {
    .add-btn {
      display: inline-block;
      width: 100%;
      margin: 15px auto 0;
      padding: 8px 0;
      border: 1px dashed #8a8a8a;
      border-radius: 0;
      font-size: 12px;

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .dialog-tip {
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
  }

  .selector-dialog, .editor-dialog {
    .editor-footer {
      display: flex;
      justify-content: space-between;
    }
  }

}

.question-list-editor.tree-question-editor.job-editor-mode {
  .question-list {
    margin-bottom: 20px;
  }

  .add-btn-group .add-btn {
    box-sizing: border-box;
    min-height: 30px;
    padding: 6px 0;
  }
}
</style>
